import React, { useEffect, useState } from 'react';
import { TextField, Button, Typography, Grid, Alert } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { EditUserProfileForm } from 'renderer/features/users-page/schemas/users.schemas';
import {
  useEditUserProfileMutation,
  useGetUserDetailsQuery,
} from 'renderer/features/users-page/services/users.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';

interface EditUserProfileProps {
  userId: string;
}

const initialForm: EditUserProfileForm = {
  firstName: '',
  lastName: '',
  organization: '',
  email: '',
};

const EditUser = ({ userId }: EditUserProfileProps) => {
  const [form, setForm] = useState<EditUserProfileForm>(initialForm);
  const {
    data: details,
    isFetching: userDetailsFetching,
    error: userDetailsError,
  } = useGetUserDetailsQuery(userId);
  const [
    editUserProfile,
    {
      isSuccess: editSuccess,
      error: editError,
      isLoading: editLoading,
      data: editResult,
    },
  ] = useEditUserProfileMutation();

  useEffect(() => {
    if (details) {
      setForm({
        firstName: details.user.firstName,
        lastName: details.user.lastName,
        organization: details.user.organization ?? '',
        email: details.user.email,
      });
    }
  }, [details]);

  const isLoading = userDetailsFetching || editLoading;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = () => {
    editUserProfile({
      ...form,
      userId,
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5">Edit User Profile</Typography>
      </Grid>
      {userDetailsError && (
        <Grid item xs={12}>
          <Alert severity="error">
            Error fetching user details: {formatQueryError(userDetailsError)}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} lg={6}>
        <TextField
          label="First Name"
          name="firstName"
          disabled={isLoading}
          value={form.firstName}
          onChange={handleInputChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Last Name"
          name="lastName"
          disabled={isLoading}
          value={form.lastName}
          onChange={handleInputChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Organization"
          name="organization"
          disabled={isLoading}
          value={form.organization}
          onChange={handleInputChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          label="Email"
          name="email"
          disabled={isLoading}
          value={form.email}
          onChange={handleInputChange}
          fullWidth
        />
      </Grid>
      {editError && (
        <Grid item xs={12}>
          <Alert severity="error">
            Error editing user profile: {formatQueryError(editError)}
          </Alert>
        </Grid>
      )}
      {editSuccess && editResult && (
        <Grid item xs={12}>
          <Alert severity="success">{editResult.message}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={isLoading}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      </Grid>
    </Grid>
  );
};

export default EditUser;
